import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Breadcrumbs from '../../../components/BreadCrumbs';
import SearchBar from '../../../components/searchBar/SearchBar';
import Dropdown from '../../../components/inputFields/DropDown';
import DateRangePicker from '../../../components/inputFields/DateRangePicker';
import Table from '../../../components/tables/Table';
import Pagination from '../../../components/tables/pagination/Pagination';
import CustomLoading from '../../../components/customLoading/CustomLoading';
import B2BLeadDetail from '../../../components/leads/b2b/B2BLeadDetail';
import ShowModal from '../../../utils/ShowModal';
import { UserPlus } from 'lucide-react';
import { getPaidB2BDealers, getB2BSalespersonList, bulkAssignB2BLeads } from '../../../services/b2bLeads';
import { districtApi, stateApi, subcategoriesCRUDUrl } from '../../../constants/apis';
import { getUserToken, getUserRole } from '../../../helpers/local_storage_helpers';
import { normalizePaginatedResponse } from '../../../utils/pagination';
import { formatDateMonth } from '../../../utils/FormatDateTime';

const authHeaders = () => ({ headers: { Authorization: `Token ${getUserToken()}` } });

const SORT_FIELD_MAP = {
    orders: 'total_b2b_orders',
    order_value: 'total_order_value',
};

const formatMoney = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return '—';
    return `₹${num.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const PaidB2BDealers = () => {
    // Salespeople only view dealers — assignment management is admin-only.
    const canManageAssignment = getUserRole() === 'admin';
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ results: [], count: 0 });
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 30;
    const cancelTokenRef = useRef(null);

    const [states, setStates] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [salespeople, setSalespeople] = useState([]);
    const [selectedDealer, setSelectedDealer] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [assignModal, setAssignModal] = useState({ open: false, leadIds: [] });
    const [assignForm, setAssignForm] = useState({ sales_person: '', remarks: '' });
    const [assigning, setAssigning] = useState(false);
    const [sort, setSort] = useState({ key: null, dir: null });

    const [filters, setFilters] = useState({
        search: '',
        state: '',
        district: '',
        product_sub_category: '',
        sales_person: '',
        start_date: '',
        end_date: '',
    });

    useEffect(() => {
        const fetchSubcategories = async () => {
            try {
                const res = await axios.get(`${subcategoriesCRUDUrl}?page_size=1000`, authHeaders());
                if (res.status === 200) setSubcategories(res.data.results || res.data);
            } catch (error) {
                console.error('Error fetching subcategories:', error);
            }
        };
        const fetchStates = async () => {
            try {
                const res = await axios.get(stateApi, authHeaders());
                if (res.status === 200) setStates(res.data);
            } catch (error) {
                console.error('Error fetching states:', error);
            }
        };
        const fetchSalespeople = async () => {
            try {
                const res = await getB2BSalespersonList();
                if (res.status === 200) {
                    const list = res.data.results || res.data || [];
                    setSalespeople(list.map(user => ({
                        label: user.name || user.username || user.email,
                        value: user.id,
                    })));
                }
            } catch (error) {
                console.error('Error fetching salespeople:', error);
            }
        };
        fetchSubcategories();
        fetchStates();
        fetchSalespeople();
    }, []);

    const fetchDealers = useCallback(async () => {
        if (cancelTokenRef.current) cancelTokenRef.current.cancel();
        cancelTokenRef.current = axios.CancelToken.source();

        setLoading(true);
        try {
            let query = `page=${currentPage}&page_size=${pageSize}`;
            if (filters.search) query += `&search=${filters.search}`;
            if (filters.state) query += `&state=${filters.state}`;
            if (filters.district) query += `&district=${filters.district}`;
            if (filters.product_sub_category) query += `&product_sub_category=${filters.product_sub_category}`;
            if (filters.sales_person) query += `&sales_person=${filters.sales_person}`;
            if (filters.start_date) query += `&start_date=${filters.start_date}`;
            if (filters.end_date) query += `&end_date=${filters.end_date}`;
            if (sort.key && sort.dir) {
                const field = SORT_FIELD_MAP[sort.key];
                if (field) query += `&ordering=${sort.dir === 'desc' ? `-${field}` : field}`;
            }

            const res = await getPaidB2BDealers(query);
            if (res?.status === 200) {
                setData(normalizePaginatedResponse(res.data));
                setSelectedIds([]);
            } else {
                toast.error(res?.data?.detail || 'Failed to fetch paid B2B dealers');
            }
        } catch (error) {
            if (!axios.isCancel(error)) toast.error('Error connecting to server');
        } finally {
            setLoading(false);
        }
    }, [currentPage, filters, sort]);

    useEffect(() => {
        fetchDealers();
    }, [fetchDealers]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const handleSortChange = ({ key, filter }) => {
        setSort({ key: filter === 'none' ? null : key, dir: filter === 'none' ? null : filter });
        setCurrentPage(1);
    };

    const handleStateChange = async (val) => {
        handleFilterChange('state', val);
        const selectedState = states.find(s => String(s.id) === String(val));
        if (selectedState) {
            try {
                const res = await axios.get(`${districtApi}${selectedState.name}`, authHeaders());
                if (res.status === 200) setDistricts(res.data);
            } catch (error) {
                setDistricts([]);
            }
        } else {
            setDistricts([]);
        }
    };

    const resetFilters = () => {
        setFilters({ search: '', state: '', district: '', product_sub_category: '', sales_person: '', start_date: '', end_date: '' });
        setDistricts([]);
        setCurrentPage(1);
    };

    const toggleRowSelection = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const allPageSelected = data.results?.length > 0 && data.results.every(item => selectedIds.includes(item.id));

    const togglePageSelection = () => {
        if (allPageSelected) {
            setSelectedIds(prev => prev.filter(id => !data.results.some(item => item.id === id)));
        } else {
            setSelectedIds(prev => Array.from(new Set([...prev, ...(data.results || []).map(item => item.id)])));
        }
    };

    const openAssignModal = (leadIds) => {
        setAssignForm({ sales_person: '', remarks: '' });
        setAssignModal({ open: true, leadIds });
    };

    const closeAssignModal = () => {
        setAssignModal({ open: false, leadIds: [] });
        setAssignForm({ sales_person: '', remarks: '' });
    };

    const handleAssignLeads = async () => {
        if (!assignForm.sales_person) {
            toast.error('Please select a salesperson');
            return;
        }
        setAssigning(true);
        try {
            const res = await bulkAssignB2BLeads({
                lead_ids: assignModal.leadIds,
                sales_person: assignForm.sales_person,
                remarks: assignForm.remarks || undefined,
            });
            if (res.status === 200 || res.status === 201) {
                const d = res.data || {};
                toast.success(`Assigned ${d.assigned_count || assignModal.leadIds.length} dealer(s)`);
                closeAssignModal();
                setSelectedIds([]);
                fetchDealers();
            } else {
                toast.error(res.data?.detail || 'Failed to assign dealers');
            }
        } catch (error) {
            toast.error('Error assigning dealers');
        } finally {
            setAssigning(false);
        }
    };

    const headers = [
        ...(canManageAssignment
            ? [{ label: <input type="checkbox" checked={allPageSelected} onChange={togglePageSelection} className="accent-primary" />, key: 'select', width: '34px' }]
            : []),
        { label: 'Shop/Owner', key: 'shop_owner' },
        { label: 'Location', key: 'location' },
        { label: 'Sub-Categories', key: 'product_subcategory', width: '150px' },
        { label: 'Orders', key: 'orders', width: '70px', needFilter: true },
        { label: 'Order Value', key: 'order_value', width: '110px', needFilter: true },
        { label: 'Collected / Due', key: 'collected_due', width: '120px' },
        { label: 'Last Order', key: 'last_order_date', width: '95px' },
        { label: 'Last Called', key: 'last_called_date', width: '95px' },
        { label: 'Assigned To', key: 'assigned_to', width: '140px' },
        ...(canManageAssignment
            ? [
                { label: 'Sales Persons', key: 'sales_persons', width: '160px' },
                { label: 'Actions', key: 'actions', width: '90px' },
            ]
            : []),
    ];

    const rows = (data.results || []).map((item) => ({
        select: (
            <input
                type="checkbox"
                checked={selectedIds.includes(item.id)}
                onChange={() => toggleRowSelection(item.id)}
                className="accent-primary"
            />
        ),
        shop_owner: (
            <button onClick={() => setSelectedDealer(item)} className="text-left w-full">
                <div className="text-xs font-black text-gray-800 hover:text-primary">{item.shop_name || ''}</div>
                <div className="text-[10px] text-gray-500">{item.owner_name || ''}</div>
                <div className="text-[10px] font-bold text-primary">{item.phone || '—'}</div>
            </button>
        ),
        location: (
            <div className="text-xs text-gray-600">
                <div>{item.district_name || '—'} / {item.state_name || '—'}</div>
                {item.area && <div className="text-[10px] text-gray-400">{item.area}</div>}
            </div>
        ),
        product_subcategory: (() => {
            const subs = item.product_sub_categories || item.product_sub_category || item.product_subcategory || [];
            return (
                <div className="flex flex-wrap gap-1 max-w-[150px]">
                    {subs.map((sc, idx) => (
                        <span key={sc.id ?? sc ?? idx} className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded-full text-[9px] font-bold">
                            {sc.name || sc}
                        </span>
                    ))}
                    {!subs.length && <span className="text-[10px] text-gray-400">—</span>}
                </div>
            );
        })(),
        orders: (
            <span className="text-xs font-bold text-gray-700">{item.total_b2b_orders ?? '—'}</span>
        ),
        order_value: (
            <div className="flex flex-col gap-0.5">
                <span className="text-xs font-bold text-gray-700" title={formatMoney(item.total_order_value)}>
                    {item.total_order_value_words || formatMoney(item.total_order_value)}
                </span>
                <span className="text-[10px] text-gray-400" title={formatMoney(item.average_order_value)}>
                    Avg: {formatMoney(item.average_order_value)}
                </span>
            </div>
        ),
        collected_due: (
            <div className="flex flex-col gap-0.5">
                <span className="text-xs font-bold text-emerald-700" title={formatMoney(item.total_collected)}>
                    {item.total_collected_words || formatMoney(item.total_collected)}
                </span>
                <span className={`text-[10px] font-bold ${Number(item.due_amount) > 0 ? 'text-rose-600' : 'text-gray-400'}`} title={formatMoney(item.due_amount)}>
                    Due: {item.due_amount_words || formatMoney(item.due_amount)}
                </span>
            </div>
        ),
        last_order_date: (
            <span className="text-[10px] font-medium text-gray-600">{item.last_order_date || '—'}</span>
        ),
        last_called_date: (
            <span className="text-[10px] font-medium text-gray-600">
                {item.last_called_date ? formatDateMonth(item.last_called_date) : '—'}
            </span>
        ),
        assigned_to: item.assigned_to ? (
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-1 max-w-[135px]">
                <span className="w-5 h-5 rounded-full bg-primary text-white text-[10px] font-black flex items-center justify-center shrink-0">
                    {(item.assigned_to.name || '?').charAt(0).toUpperCase()}
                </span>
                <span className="text-[11px] font-bold text-emerald-800 truncate">{item.assigned_to.name}</span>
            </div>
        ) : (
            <span className="text-[11px] italic text-gray-400">Unassigned</span>
        ),
        sales_persons: (() => {
            const names = item.sales_person_names || item.sales_persons || item.salespersons || [];
            return names.length > 0 ? (
                <div className="flex flex-wrap gap-1 max-w-[155px]">
                    {names.map((name, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 bg-blue-50 border border-blue-100 rounded-full px-2 py-0.5">
                            <span className="w-4 h-4 rounded-full bg-primary text-white text-[9px] font-black flex items-center justify-center shrink-0">
                                {(name?.name || name || '?').charAt(0).toUpperCase()}
                            </span>
                            <span className="text-[10px] font-bold text-blue-800 truncate">{name?.name || name}</span>
                        </span>
                    ))}
                </div>
            ) : (
                <span className="text-[11px] italic text-gray-400">—</span>
            );
        })(),
        actions: (
            <button
                onClick={() => openAssignModal([item.id])}
                className="inline-flex items-center gap-1 px-2 py-1 bg-primary text-white rounded-lg text-[10px] font-black"
                title="Assign to salesperson"
            >
                <UserPlus size={12} /> Assign
            </button>
        ),
        _original: item,
    }));

    return (
        <div className="space-y-3">
            <div>
                <Breadcrumbs items={[{ name: 'Home', link: '/dashboard' }, { name: 'Sales' }, { name: 'Paid B2B Dealers' }]} />
                <h1 className="text-2xl font-black text-gray-800 mt-2">Paid B2B Dealers</h1>
                <p className="text-xs font-bold text-gray-500">{data.count} dealers with at least one B2B order</p>
            </div>

            <div className="bg-white rounded-xl p-4 border border-gray-200/60 shadow-sm space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="form-control w-full">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                            <SearchBar placeholder="Search shop/owner/phone..." onSearch={(val) => handleFilterChange('search', val)} value={filters.search} compact />
                        </div>
                        <Dropdown
                            label="State"
                            options={states.map(s => ({ label: s.name, value: s.id }))}
                            value={filters.state}
                            onChange={handleStateChange}
                            needSearch
                            placeholder="Region"
                            compact
                        />
                        <Dropdown
                            label="District"
                            options={districts.map(d => ({ label: d.name, value: d.id }))}
                            value={filters.district}
                            onChange={(val) => handleFilterChange('district', val)}
                            needSearch
                            placeholder="District"
                            disabled={!filters.state}
                            compact
                        />
                        <Dropdown
                            label="Product Sub-Category"
                            options={subcategories.map(sc => ({ label: sc.name, value: sc.name }))}
                            value={filters.product_sub_category}
                            onChange={(val) => handleFilterChange('product_sub_category', val)}
                            placeholder="All Sub-Categories"
                            needSearch
                            compact
                        />
                        {canManageAssignment && (
                            <Dropdown
                                label="Sales Person"
                                options={salespeople}
                                value={filters.sales_person}
                                onChange={(val) => handleFilterChange('sales_person', val)}
                                placeholder="All Salespeople"
                                needSearch
                                compact
                            />
                        )}
                        <div className="space-y-1.5 sm:col-span-2 md:col-span-1">
                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Date Range</label>
                            <DateRangePicker
                                compact
                                placeholder="Select date range"
                                value={{ start: filters.start_date, end: filters.end_date }}
                                onChange={({ start, end }) => {
                                    setFilters(prev => ({ ...prev, start_date: start || '', end_date: end || '' }));
                                    setCurrentPage(1);
                                }}
                            />
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button onClick={resetFilters} className="text-[10px] font-black text-rose-600 hover:text-rose-700 uppercase tracking-wider">
                            Reset All
                        </button>
                    </div>
                </div>

            {canManageAssignment && selectedIds.length > 0 && (
                <div className="bg-white border border-emerald-100 shadow-sm rounded-xl px-4 py-3 flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="text-sm font-black text-gray-800">
                        {selectedIds.length} dealer{selectedIds.length === 1 ? '' : 's'} selected
                    </div>
                    <button
                        onClick={() => openAssignModal(selectedIds)}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-xs font-black shadow-sm hover:bg-primary/90"
                    >
                        <UserPlus size={14} /> Assign to salesperson
                    </button>
                </div>
            )}

            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                {loading ? (
                    <div className="flex justify-center py-20"><CustomLoading size={80} /></div>
                ) : (
                    <>
                        <Table
                            headers={headers}
                            rows={rows}
                            onFilterChange={handleSortChange}
                            currentSort={sort.key}
                            currentSortDir={sort.dir}
                        />
                        {data.results?.length === 0 && (
                            <div className="text-center py-10 text-gray-500">No paid B2B dealers found matching your criteria.</div>
                        )}
                        <div className="p-4 border-t">
                            <Pagination
                                currentPage={currentPage}
                                totalEntries={data.count}
                                onPageChange={setCurrentPage}
                                pageSize={pageSize}
                                data={data}
                            />
                        </div>
                    </>
                )}
            </div>

            {selectedDealer && (
                <B2BLeadDetail
                    lead={selectedDealer}
                    onClose={() => setSelectedDealer(null)}
                    onUpdated={fetchDealers}
                />
            )}

            <ShowModal
                isOpen={assignModal.open}
                onRequestClose={closeAssignModal}
                title={`Assign ${assignModal.leadIds.length} dealer${assignModal.leadIds.length === 1 ? '' : 's'}`}
                modalWidth="420px"
                componentToPass={
                    <div className="space-y-4">
                        <Dropdown
                            label="Salesperson"
                            options={salespeople}
                            value={assignForm.sales_person}
                            onChange={(val) => setAssignForm(prev => ({ ...prev, sales_person: val }))}
                            placeholder="Select salesperson"
                            needSearch
                        />
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                            <textarea
                                value={assignForm.remarks}
                                onChange={(e) => setAssignForm(prev => ({ ...prev, remarks: e.target.value }))}
                                rows={3}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                                placeholder="Optional assignment note"
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <button onClick={closeAssignModal} className="px-4 py-2 text-xs font-bold border border-gray-200 rounded-lg text-gray-600">Cancel</button>
                            <button
                                onClick={handleAssignLeads}
                                disabled={assigning}
                                className="px-5 py-2 text-xs font-black bg-primary text-white rounded-lg disabled:bg-gray-300"
                            >
                                {assigning ? 'Assigning...' : 'Assign'}
                            </button>
                        </div>
                    </div>
                }
            />
        </div>
    );
};

export default PaidB2BDealers;
